import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAction, useMutation, useQuery } from "convex/react";
import { api, type Id } from "../lib/api";
import { useCompany } from "../lib/company";
import { routeIdParam } from "../domain/routeParams";
import { caughtErrorMessage } from "../domain/errors";
import { formatAuDate } from "../domain/time";
import { Button, Card, Icon, JurisBadge, cx } from "../components/ui";
import { RichText } from "../components/RichText";
import type { ChangeType, PolicyImpactLevel, PolicyReviewStatus } from "../../convex/model/enums";
import {
  canCloseReviewAsNoPolicyChange,
  emptyPolicyReviewDraftState,
  emptyPolicyReviewNoChangeDecisionState,
  emptyPolicySuggestionWorkflowState,
  nextPolicyReviewDraftState,
  nextPolicyReviewNoChangeDecisionState,
  nextPolicySuggestionWorkflowState,
  policyChangeTypeLabel,
  policyChangeTypeToneClass,
  policyReviewActionStateMessageToneClass,
  policyReviewDraftToneClass,
  policyImpactLevelLabel,
  policyImpactLevelToneClass,
  policyReviewDecisionErrorMessage,
  policyReviewChecklistView,
  policyReviewNoChangeDecisionView,
  policyReviewNoChangeCloseCommand,
  policyReviewOwnerSummaryRows,
  policyReviewStartActionVisible,
  policyReviewStartCommand,
  policyReviewStatusLabel,
  policyReviewWorkbenchView,
  policySuggestionActionLabel,
  policyReviewStatusToneClass,
  policyReviewUpdateDecisionView,
} from "../domain/policyReview";
import { nextPolicySectionId } from "../domain/policyEditor";
import { isEmptyDoc, parseRichTextDocResult, type RichTextDoc } from "../domain/richText";

export function PolicyReview() {
  const params = useParams();
  const navigate = useNavigate();
  const id = routeIdParam<"policyImpactMatches">(params, "id");
  const { companyId } = useCompany();
  const review = useQuery(api.policies.getImpactReview, companyId !== null && id !== undefined ? { companyId, matchId: id } : "skip");
  const setImpactStatus = useMutation(api.policies.setImpactStatus);
  const updatePolicySection = useMutation(api.policies.updatePolicySection);
  const proposePolicyUpdate = useAction(api.ai.proposePolicyUpdate);
  const now = Date.now();

  const [activeSectionId, setActiveSectionId] = useState<Id<"companyPolicySections"> | null>(null);
  const [policyDraft, setPolicyDraft] = useState(() => emptyPolicyReviewDraftState());
  const [noChangeDecision, setNoChangeDecision] = useState(() => emptyPolicyReviewNoChangeDecisionState());
  const [suggestionWorkflow, setSuggestionWorkflow] = useState(() => emptyPolicySuggestionWorkflowState());
  const aiState = suggestionWorkflow.suggestionState;
  const aiError = suggestionWorkflow.suggestionError;

  const workbench = policyReviewWorkbenchView({
    review,
    activeSectionId,
    draftBody: policyDraft.body,
    saveState: policyDraft.saveState,
    saveError: policyDraft.saveError,
    hasCompany: companyId !== null,
    hasReview: id !== undefined,
    suggestionState: aiState,
  });
  const {
    policySections,
    selectedSection,
    suggestedText,
    hasSuggestedText,
    draft: draftView,
    canSavePolicyUpdate,
    canGeneratePolicySuggestion: canGeneratePolicySuggestionNow,
  } = workbench;
  const draftChanged = draftView.changed;
  const draftBody = policyDraft.body;
  const saveState = policyDraft.saveState;
  const decisionState = noChangeDecision.decisionState;
  const decisionError = noChangeDecision.decisionError;
  const noChangeReason = noChangeDecision.reason;
  const canCloseNoPolicyChange = canCloseReviewAsNoPolicyChange({
    reviewStatus: review?.status,
    decisionState,
    noChangeReason,
  });

  useEffect(() => {
    const nextSectionId = nextPolicySectionId({ sections: policySections, activeSectionId });
    if (nextSectionId !== activeSectionId) setActiveSectionId(nextSectionId);
  }, [policySections, activeSectionId]);

  useEffect(() => {
    setPolicyDraft((current) => nextPolicyReviewDraftState(current, {
      type: "sectionLoaded",
      body: selectedSection?.body ?? "",
    }));
    setSuggestionWorkflow((current) => nextPolicySuggestionWorkflowState(current, { type: "reset" }));
  }, [selectedSection?._id, selectedSection?.body]);

  useEffect(() => {
    setNoChangeDecision((current) => nextPolicyReviewNoChangeDecisionState(current, {
      type: "reviewLoaded",
      reason: review?.decisionNote ?? "",
    }));
  }, [review?._id, review?.decisionNote]);

  const startReview = () => {
    if (companyId === null || id === undefined || review === null || review === undefined) return;
    const command = policyReviewStartCommand({ matchId: id, currentStatus: review.status });
    if (command.kind === "blocked") return;

    setNoChangeDecision((current) => nextPolicyReviewNoChangeDecisionState(current, { type: "statusReset" }));
    void setImpactStatus({ companyId, matchId: command.matchId, status: command.status });
  };

  const updateDraftBody = (body: string) => {
    setPolicyDraft((current) => nextPolicyReviewDraftState(current, { type: "bodyInput", body }));
  };

  const updateNoChangeReason = (reason: string) => {
    setNoChangeDecision((current) => nextPolicyReviewNoChangeDecisionState(current, { type: "reasonInput", reason }));
  };

  const generatePolicySuggestion = async () => {
    if (companyId === null || id === undefined || selectedSection === null || !canGeneratePolicySuggestionNow) return;
    setSuggestionWorkflow((current) => nextPolicySuggestionWorkflowState(current, { type: "generationStarted" }));
    try {
      const suggestion = await proposePolicyUpdate({
        companyId,
        matchId: id,
        policySectionId: selectedSection._id,
      });
      updateDraftBody(suggestion.proposedPolicyText);
      setSuggestionWorkflow((current) => nextPolicySuggestionWorkflowState(current, { type: "generationSucceeded" }));
    } catch (e) {
      setSuggestionWorkflow((current) => nextPolicySuggestionWorkflowState(current, {
        type: "generationFailed",
        error: caughtErrorMessage(e, "Could not generate a document suggestion"),
      }));
    }
  };

  const savePolicyUpdate = async () => {
    if (companyId === null || id === undefined || selectedSection === null || !canSavePolicyUpdate) return;
    setPolicyDraft((current) => nextPolicyReviewDraftState(current, { type: "saveStarted" }));
    try {
      await updatePolicySection({
        companyId,
        sectionId: selectedSection._id,
        body: draftBody,
        matchId: id,
        markUpdated: true,
      });
      setPolicyDraft((current) => nextPolicyReviewDraftState(current, { type: "saveSucceeded" }));
      setNoChangeDecision((current) => nextPolicyReviewNoChangeDecisionState(current, { type: "saveSucceeded" }));
    } catch (e) {
      setPolicyDraft((current) => nextPolicyReviewDraftState(current, {
        type: "saveFailed",
        error: caughtErrorMessage(e, "Could not save document update"),
      }));
    }
  };

  const closeNoPolicyChange = async () => {
    if (companyId === null || id === undefined || review === null || review === undefined) return;
    const command = policyReviewNoChangeCloseCommand({
      matchId: id,
      decisionNote: noChangeReason,
    });
    if (!command.ok) {
      setNoChangeDecision((current) => nextPolicyReviewNoChangeDecisionState(current, {
        type: "saveFailed",
        error: policyReviewDecisionErrorMessage(command.error),
      }));
      return;
    }

    setNoChangeDecision((current) => nextPolicyReviewNoChangeDecisionState(current, { type: "saveStarted" }));
    try {
      await setImpactStatus({
        companyId,
        ...command.value,
      });
      setNoChangeDecision((current) => nextPolicyReviewNoChangeDecisionState(current, { type: "saveSucceeded" }));
    } catch (e) {
      setNoChangeDecision((current) => nextPolicyReviewNoChangeDecisionState(current, {
        type: "saveFailed",
        error: caughtErrorMessage(e, "Could not close this document review"),
      }));
    }
  };
  if (review === undefined) {
    return <p className="px-9 py-7 text-slate-500">Loading document review...</p>;
  }

  if (review === null) {
    return (
      <div className="mx-auto max-w-[720px] px-6 py-12 text-center">
        <div className="mx-auto mb-3 grid h-11 w-11 place-items-center rounded-full bg-slate-100 text-slate-400"><Icon name="info" size={21} /></div>
        <h2 className="font-serif text-[23px] font-semibold text-slate-900">Document review not found</h2>
        <p className="mt-2 text-sm text-slate-500">This review may have been removed or may belong to another company.</p>
        <div className="mt-5"><Link to="/"><Button variant="outline" icon="arrowRight">Back to dashboard</Button></Link></div>
      </div>
    );
  }

  const policy = review.policy;
  const update = review.update;
  const legislation = review.legislation;
  const updateMarkup = updateMarkupDoc(update?.markup);
  const sourceSectionId = update?.appliesToSectionIds[0] ?? null;
  const sourceLegislationPath = legislation === null
    ? null
    : "/legislation/" + legislation._id + (sourceSectionId === null ? "" : "?section=" + sourceSectionId);
  const checklistView = policyReviewChecklistView({ reviewStatus: review.status, saveState });
  const updateDecisionView = policyReviewUpdateDecisionView({
    reviewStatus: review.status,
    draftChanged,
    saveState,
  });
  const noChangeDecisionView = policyReviewNoChangeDecisionView({
    reviewStatus: review.status,
    decisionState,
  });
  const ownerSummaryRows = policyReviewOwnerSummaryRows({
    policy,
    review,
    nowMs: now,
  });

  return (
    <div className="mx-auto w-full max-w-[1120px] px-6 py-7 lg:px-9">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-5">
        <div className="min-w-0">
          <button onClick={() => navigate(-1)} className="mb-3 inline-flex items-center gap-1.5 text-[12.5px] font-medium text-slate-500 hover:text-[var(--accent-dark)]">
            <Icon name="arrowRight" size={13} className="rotate-180" /> Back
          </button>
          <div className="flex flex-wrap items-center gap-2">
            <ReviewStatusBadge status={review.status} />
            <ImpactBadge level={review.impactLevel} />
            {legislation !== null && <JurisBadge code={legislation.jurisdiction} />}
          </div>
          <h2 className="mt-3 font-serif text-[30px] font-semibold leading-tight tracking-tight text-slate-950">
            {policy?.title ?? "Document review"}
          </h2>
          <p className="mt-2 max-w-[760px] text-[14px] leading-relaxed text-slate-500">
            Review the changed obligation, decide whether the mapped document still covers it, then close the evidence record.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" icon="layers" onClick={() => navigate(policy === null ? "/documents" : "/mappings/document/" + policy._id)}>Review document mappings</Button>
          {policyReviewStartActionVisible(review.status) && <Button variant="outline" icon="pencil" onClick={startReview}>Start review</Button>}
        </div>
      </div>

      <section className="grid gap-5 lg:grid-cols-[1fr_22rem]">
        <div className="space-y-5">
          <Card className="overflow-hidden">
            <div className="border-b border-slate-100 px-5 py-3.5">
              <h3 className="text-[14px] font-semibold text-slate-900">Changed obligation</h3>
              <p className="mt-0.5 text-[12.5px] text-slate-400">The monitored legal text that reopened this mapping review.</p>
            </div>
            <div className="px-5 py-5">
              <div className="flex flex-wrap items-center gap-2">
                {legislation !== null && <JurisBadge code={legislation.jurisdiction} />}
                <span className="font-mono text-[12px] text-slate-400">{legislation?.reference ?? "-"}</span>
                <ChangePill type={update?.changeType ?? "amended"} />
              </div>
              <h3 className="mt-3 text-[17px] font-semibold text-slate-900">{legislation?.title ?? "Legislation"}</h3>
              <p className="mt-2 text-[14px] leading-relaxed text-slate-600">{update?.summary ?? "A monitored legislative update affected this document area."}</p>
              {updateMarkup !== null && (
                <div className="mt-4 rounded-xl border border-slate-200 bg-white px-4 py-3">
                  <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Changed legal text</div>
                  <div className="max-h-[26rem] overflow-auto text-[13.5px] leading-relaxed text-slate-700">
                    <RichText doc={updateMarkup} legal />
                  </div>
                </div>
              )}
              <div className="mt-4 grid gap-3 text-[13px] md:grid-cols-2">
                <InfoBlock label="Amending instrument" value={update?.amendingLegislation ?? "Unknown"} />
                <InfoBlock label="Commencement" value={update === null ? "Unknown" : formatAuDate(update.commencementDate)} />
              </div>
            </div>
          </Card>

          <Card className="overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-3.5">
              <div>
                <h3 className="text-[14px] font-semibold text-slate-900">Mapping workbench</h3>
                <p className="mt-0.5 text-[12.5px] text-slate-400">Review the document clause Lawstream matched to this changed obligation.</p>
              </div>
              {selectedSection !== null && (
                <Button size="sm" variant="outline" icon="spark" disabled={!canGeneratePolicySuggestionNow} onClick={generatePolicySuggestion}>
                  {policySuggestionActionLabel({ suggestionState: aiState, hasSuggestedText, context: "toolbar" })}
                </Button>
              )}
            </div>
            <div className="px-5 py-5">
              {selectedSection === null ? (
                <div className="rounded-lg bg-slate-50 px-4 py-4 text-[13.5px] text-slate-500 ring-1 ring-slate-100">
                  This impact is linked to the company document, but no specific document section has been stored yet.
                </div>
              ) : (
                <div className="space-y-4">
                  {policySections.length > 1 && (
                    <div className="flex flex-wrap gap-2">
                      {policySections.map((section) => (
                        <button
                          key={section._id}
                          onClick={() => setActiveSectionId(section._id)}
                          className={cx(
                            "rounded-lg px-3 py-1.5 text-[12.5px] font-medium ring-1",
                            selectedSection._id === section._id
                              ? "bg-[var(--accent-soft)] text-[var(--accent-dark)] ring-[color:var(--accent)]/20"
                              : "bg-white text-slate-500 ring-slate-200 hover:bg-slate-50",
                          )}
                        >
                          {section.heading}
                        </button>
                      ))}
                    </div>
                  )}

                  <div className="rounded-xl border border-slate-200 bg-white">
                    <div className="border-b border-slate-100 bg-slate-50/70 px-4 py-3">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Current document clause</div>
                      <h3 className="mt-1 text-[15px] font-semibold text-slate-900">{selectedSection.heading}</h3>
                    </div>
                    <div className="px-4 py-4 text-[14px] leading-relaxed text-slate-700">{selectedSection.body}</div>
                  </div>

                  <div className="grid gap-4 lg:grid-cols-2">
                    <div className="rounded-xl bg-[var(--accent-soft)] px-4 py-3 ring-1 ring-[color:var(--accent)]/20">
                      <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[var(--accent-dark)]">Suggested replacement</div>
                      <p className="text-[13.5px] leading-relaxed text-slate-700">
                        {hasSuggestedText ? suggestedText : "Generate a clause suggestion from the matched document wording and legislative update."}
                      </p>
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        <Button size="sm" variant="soft" icon="arrowRight" disabled={!hasSuggestedText || aiState === "generating"} onClick={() => updateDraftBody(suggestedText)}>
                          Use suggestion
                        </Button>
                        <Button size="sm" variant="outline" icon="spark" disabled={!canGeneratePolicySuggestionNow} onClick={generatePolicySuggestion}>
                          {policySuggestionActionLabel({ suggestionState: aiState, hasSuggestedText, context: "replacement" })}
                        </Button>
                      </div>
                      {aiState === "generated" && <p className="mt-2 text-[12.5px] text-[var(--accent-dark)]">Suggestion added to the draft box for review.</p>}
                      {aiState === "error" && <p className="mt-2 text-[12.5px] text-red-600">{aiError ?? "Could not generate a document suggestion."}</p>}
                    </div>
                    <div className="rounded-xl bg-slate-50 px-4 py-3 ring-1 ring-slate-200">
                      <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Review notes</div>
                      <p className="text-[13.5px] leading-relaxed text-slate-600">{review.suggestedAction}</p>
                    </div>
                  </div>

                  <div>
                    <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                      <label className="text-[12px] font-semibold uppercase tracking-wide text-slate-400" htmlFor="policy-draft">Updated document wording</label>
                      <button className="text-[12.5px] font-medium text-slate-500 hover:text-slate-800" onClick={() => updateDraftBody(selectedSection.body)}>Reset to current</button>
                    </div>
                    <textarea
                      id="policy-draft"
                      value={draftBody}
                      onChange={(e) => updateDraftBody(e.target.value)}
                      className="min-h-48 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-[14px] leading-relaxed text-slate-800 shadow-sm focus:border-[color:var(--accent)] focus:outline-none focus:ring-1 focus:ring-[color:var(--accent)]"
                    />
                    <div className="mt-3 flex flex-wrap items-center gap-3">
                      <Button icon="check" disabled={!canSavePolicyUpdate} onClick={savePolicyUpdate}>
                        {saveState === "saving" ? "Saving..." : "Save document update"}
                      </Button>
                      <span className={cx("text-[12.5px]", policyReviewDraftToneClass(draftView.tone))}>
                        {draftView.message}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Card>

          <Card className="overflow-hidden">
            <div className="border-b border-slate-100 px-5 py-3.5">
              <h3 className="text-[14px] font-semibold text-slate-900">Why Lawstream matched this section</h3>
              <p className="mt-0.5 text-[12.5px] text-slate-400">Matching rationale and document terms used for review triage.</p>
            </div>
            <div className="px-5 py-5">
              <p className="text-[14px] leading-relaxed text-slate-700">{review.rationale}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {review.matchedTerms.map((term) => (
                  <span key={term} className="rounded bg-slate-50 px-2 py-1 text-[12px] font-medium text-slate-600 ring-1 ring-slate-200">{term}</span>
                ))}
              </div>
            </div>
          </Card>

          <Card className="overflow-hidden">
            <div className="border-b border-slate-100 px-5 py-3.5">
              <h3 className="text-[14px] font-semibold text-slate-900">Review checklist</h3>
              <p className="mt-0.5 text-[12.5px] text-slate-400">Track the document update through to completion.</p>
            </div>
            <div className="px-5 py-5">
              <div className="grid gap-3 md:grid-cols-3">
                <ChecklistItem label="Read change" done={checklistView.readChangeDone} />
                <ChecklistItem label="Edit document" done={checklistView.editPolicyDone} />
                <ChecklistItem label="Close review" done={checklistView.closeReviewDone} />
              </div>
            </div>
          </Card>
        </div>

        <aside className="space-y-5">
          <Card className="p-5">
            <div className="mb-4 flex items-start gap-3">
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-[var(--accent-soft)] text-[var(--accent-dark)] ring-1 ring-[color:var(--accent)]/20">
                <Icon name="check" size={17} />
              </span>
              <div>
                <h3 className="text-[14px] font-semibold text-slate-900">Decision</h3>
                <p className="mt-0.5 text-[12.5px] leading-relaxed text-slate-500">Choose one outcome and leave enough evidence for the audit trail.</p>
              </div>
            </div>

            <div className="space-y-3">
              <DecisionOption
                icon="pencil"
                title="Update document"
                text="Save amended wording and close this review as updated."
                active={updateDecisionView.active}
              >
                <Button size="sm" icon="check" disabled={!canSavePolicyUpdate} onClick={savePolicyUpdate}>
                  {updateDecisionView.buttonLabel}
                </Button>
              </DecisionOption>

              <DecisionOption
                icon="check"
                title="No document change"
                text="Close the review only when the existing document remains adequate."
                active={noChangeDecisionView.active}
              >
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400" htmlFor="no-change-reason">Reason</label>
                <textarea
                  id="no-change-reason"
                  value={noChangeReason}
                  onChange={(event) => updateNoChangeReason(event.target.value)}
                  placeholder="Why does this change not require a document update?"
                  className="mb-2 min-h-20 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-[13px] leading-relaxed text-slate-800 shadow-sm focus:border-[color:var(--accent)] focus:outline-none focus:ring-1 focus:ring-[color:var(--accent)]"
                />
                <Button size="sm" variant="outline" icon="check" disabled={!canCloseNoPolicyChange} onClick={closeNoPolicyChange}>
                  {noChangeDecisionView.buttonLabel}
                </Button>
              </DecisionOption>

              <DecisionOption
                icon="shield"
                title="Need coordination"
                text="Escalate larger remediation into a project with owners, tasks and evidence."
                active={false}
              >
                <Button size="sm" variant="outline" icon="shield" onClick={() => navigate("/projects")}>Open projects</Button>
              </DecisionOption>
            </div>

            {(decisionState === "saved" || decisionState === "error") && (
              <p className={cx("mt-3 text-[12.5px]", policyReviewActionStateMessageToneClass(decisionState))}>
                {decisionState === "saved"
                  ? "Decision saved."
                  : decisionError ?? "Could not save the decision."}
              </p>
            )}
          </Card>
          <Card className="p-5">
            <h3 className="text-[14px] font-semibold text-slate-900">Document owner</h3>
            <div className="mt-4 space-y-3 text-[13px] text-slate-600">
              {ownerSummaryRows.map((row) => (
                <InfoRow key={row.key} label={row.label} value={row.value} />
              ))}
            </div>
          </Card>

          <Card className="p-5">
            <h3 className="text-[14px] font-semibold text-slate-900">Source document</h3>
            <div className="mt-3 rounded-lg border border-slate-200 bg-white px-3 py-3">
              <div className="flex items-start gap-3">
                <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-slate-50 text-slate-500 ring-1 ring-slate-200"><Icon name="doc" size={16} /></span>
                <div className="min-w-0">
                  <div className="truncate text-[13.5px] font-semibold text-slate-800">{policy?.sourceName ?? "Company document"}</div>
                  <p className="mt-1 text-[12.5px] leading-snug text-slate-500">{policy?.summary ?? "Document summary unavailable."}</p>
                </div>
              </div>
            </div>
            {selectedSection !== null && (
              <div className="mt-3 rounded-lg bg-[var(--accent-soft)] px-3 py-2 text-[12.5px] font-medium text-[var(--accent-dark)] ring-1 ring-[color:var(--accent)]/20">
                Opened at {selectedSection.heading}
              </div>
            )}
            {policy !== null && (
              <Button size="sm" variant="outline" icon="pencil" className="mt-3 w-full" onClick={() => navigate("/documents/" + policy._id)}>
                Open company document
              </Button>
            )}
          </Card>

          <Card className="p-5">
            <h3 className="text-[14px] font-semibold text-slate-900">Review actions</h3>
            <div className="mt-4 grid gap-2">
              <Button variant="outline" icon="reader" onClick={() => sourceLegislationPath !== null && navigate(sourceLegislationPath)}>Read source obligation</Button>
              <Button variant="outline" icon="layers" onClick={() => navigate(policy === null ? "/documents" : "/mappings/document/" + policy._id)}>Back to document mappings</Button>
            </div>
          </Card>
        </aside>
      </section>
    </div>
  );
}

function DecisionOption({
  icon,
  title,
  text,
  active,
  children,
}: {
  icon: "check" | "pencil" | "shield";
  title: string;
  text: string;
  active: boolean;
  children: React.ReactNode;
}) {
  return (
    <section className={cx("rounded-xl border px-3 py-3", active ? "border-[color:var(--accent)] bg-[var(--accent-soft)]" : "border-slate-200 bg-white")}>
      <div className="mb-3 flex items-start gap-2.5">
        <span className={cx("mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-md ring-1", active ? "bg-white text-[var(--accent-dark)] ring-[color:var(--accent)]/20" : "bg-slate-50 text-slate-500 ring-slate-200")}>
          <Icon name={icon} size={14} />
        </span>
        <div>
          <div className="text-[13px] font-semibold text-slate-900">{title}</div>
          <p className="mt-0.5 text-[12.5px] leading-relaxed text-slate-500">{text}</p>
        </div>
      </div>
      {children}
    </section>
  );
}
function InfoBlock({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-slate-50 px-3 py-3 ring-1 ring-slate-100">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
      <div className="mt-1 text-[13px] font-medium text-slate-700">{value}</div>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-slate-400">{label}</span>
      <span className="text-right font-medium text-slate-700">{value}</span>
    </div>
  );
}

function ChecklistItem({ label, done }: { label: string; done: boolean }) {
  return (
    <div className={cx("flex items-center gap-2 rounded-lg px-3 py-2 text-[12.5px] font-medium ring-1", done ? "bg-emerald-50 text-emerald-700 ring-emerald-200" : "bg-slate-50 text-slate-500 ring-slate-200")}>
      <Icon name={done ? "check" : "clock"} size={14} />
      {label}
    </div>
  );
}

function ImpactBadge({ level }: { level: PolicyImpactLevel }) {
  return (
    <span className={cx("rounded px-1.5 py-0.5 text-[11px] font-semibold capitalize ring-1", policyImpactLevelToneClass(level))}>
      {policyImpactLevelLabel(level, "impact")}
    </span>
  );
}

function ReviewStatusBadge({ status }: { status: PolicyReviewStatus }) {
  return (
    <span className={cx("rounded px-1.5 py-0.5 text-[11px] font-semibold ring-1", policyReviewStatusToneClass(status))}>
      {policyReviewStatusLabel(status, "reviewDetail")}
    </span>
  );
}

function ChangePill({ type }: { type: ChangeType }) {
  return (
    <span className={cx("rounded px-1.5 py-0.5 text-[11px] font-semibold ring-1", policyChangeTypeToneClass(type))}>
      {policyChangeTypeLabel(type)}
    </span>
  );
}

const updateMarkupDoc = (value: unknown): RichTextDoc | null => {
  const parsed = parseRichTextDocResult(value);
  if (!parsed.ok || isEmptyDoc(parsed.value)) return null;
  return parsed.value;
};
